import { createElement, Fragment, useState } from 'react';
import { BusinessError, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ButtonProps, Dialog } from '@optimus-web/core';
import { ConfirmationDialogWithCheckAction as TConfirmationDialogWithCheckAction } from './types';

interface ControllerCheckConfirmationDialogProps {
    action: TConfirmationDialogWithCheckAction;
    component: React.ElementType;
    componentChildren: React.ReactNode;
}

const ConfirmationDialogWithCheckAction = ({
    action,
    component,
    componentChildren,
}: ControllerCheckConfirmationDialogProps) => {
    const { isDanger, buttonText, text, title, errorTitle, icon, disabled } = action;
    const [opened, setOpened] = useState(false);
    const [isAvailableToPerform, setIsAvailableToPerform] = useState(false);
    const [dialogText, setDialogText] = useState(text);
    const [dialogTitle, setDialogTitle] = useState(title);

    const handleClick = async () => {
        const { onCheck } = action;

        try {
            const response = await onCheck();
            setOpened(true);
            const { errorMessage } = response;
            if (errorMessage) {
                setDialogTitle(errorTitle);
                setDialogText(errorMessage);
            } else {
                setDialogTitle(text);
                setDialogText(title);
                setIsAvailableToPerform(true);
            }
        } catch (error) {
            throw new BusinessError(error);
        }
    };

    const handleOnCancel = () => {
        setOpened(false);
    };

    const handleOnConfirmation = () => {
        if (isAvailableToPerform) {
            const { onConfirmation } = action;
            try {
                onConfirmation();
            } catch (error) {
                throw new BusinessError(error);
            }
        }
    };

    const actions: ButtonProps[] = [
        {
            children: <LocalizedText textKey={TextKey.GoBack} />,
            onClick: handleOnCancel,
            variant: 'ghost',
            type: 'submit',
        },
        {
            children: buttonText,
            onClick: handleOnConfirmation,
            variant: isDanger ? 'destructive' : 'primary',
            disabled: !isAvailableToPerform,
            type: 'submit',
        },
    ];

    return (
        <Fragment>
            {createElement(
                component,
                {
                    title: buttonText,
                    icon,
                    onClick: handleClick,
                    disabled,
                },
                componentChildren
            )}
            <Dialog active={opened} title={dialogTitle} actions={actions} onClose={handleOnCancel}>
                {dialogText}
            </Dialog>
        </Fragment>
    );
};

export default ConfirmationDialogWithCheckAction;
